import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, Tag, Popconfirm, message, Space, Avatar, Upload } from 'antd';
import type { UploadProps, RcFile } from 'antd/es/upload';
import { PlusOutlined, TeamOutlined, SearchOutlined, UserOutlined, UploadOutlined } from '@ant-design/icons';
import {
  BotEmployee,
  AvailableBot,
  getEmployeeList,
  addEmployee,
  removeEmployee,
  updateNickname,
  updateAvatar,
  uploadAvatarImage,
  searchAvailableBots,
} from '../services/botEmployeeApi';

const COLORS = {
  primary: '#1677ff',
  secondary: '#722ed1',
};

const PageHeader: React.FC<{ title: string }> = ({ title }) => (
  <div style={{ marginBottom: 24 }}>
    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
      <TeamOutlined style={{ marginRight: 8, color: COLORS.primary }} />
      {title}
    </h2>
    <div style={{ width: 40, height: 3, background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`, borderRadius: 2, marginTop: 8 }} />
  </div>
);

const BotEmployeePage: React.FC = () => {
  const [employees, setEmployees] = useState<BotEmployee[]>([]);
  const [loading, setLoading] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [nicknameModalVisible, setNicknameModalVisible] = useState(false);
  const [avatarModalVisible, setAvatarModalVisible] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarConfirming, setAvatarConfirming] = useState(false);
  const [pendingAvatarUrl, setPendingAvatarUrl] = useState<string>('');
  const [editingEmployee, setEditingEmployee] = useState<BotEmployee | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [availableBots, setAvailableBots] = useState<AvailableBot[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addingBotId, setAddingBotId] = useState<number | null>(null);
  const [nicknameForm] = Form.useForm();

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getEmployeeList();
      setEmployees(data);
    } catch (e: any) {
      message.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  const handleSearch = async (keyword: string) => {
    setSearchLoading(true);
    try {
      const data = await searchAvailableBots(keyword || undefined);
      setAvailableBots(data);
    } catch (e: any) {
      message.error(e.message);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAdd = async (botId: number) => {
    setAddingBotId(botId);
    try {
      await addEmployee(botId);
      message.success('添加成功');
      // 从搜索结果中移除已添加的
      setAvailableBots(prev => prev.filter(b => b.id !== botId));
      fetchEmployees();
    } catch (e: any) {
      message.error(e.message);
    } finally {
      setAddingBotId(null);
    }
  };

  const handleRemove = async (id: number) => {
    try {
      await removeEmployee(id);
      message.success('移除成功');
      fetchEmployees();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleUpdateNickname = async (values: { nickname: string }) => {
    if (!editingEmployee) return;
    try {
      await updateNickname(editingEmployee.id, values.nickname);
      message.success('修改成功');
      setNicknameModalVisible(false);
      nicknameForm.resetFields();
      setEditingEmployee(null);
      fetchEmployees();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const openNicknameModal = (record: BotEmployee) => {
    setEditingEmployee(record);
    nicknameForm.setFieldsValue({ nickname: record.userName || '' });
    setNicknameModalVisible(true);
  };

  const openAvatarModal = (record: BotEmployee) => {
    setEditingEmployee(record);
    setPendingAvatarUrl('');
    setAvatarModalVisible(true);
  };

  const closeAvatarModal = () => {
    if (avatarUploading || avatarConfirming) return;
    setAvatarModalVisible(false);
    setPendingAvatarUrl('');
    setEditingEmployee(null);
  };

  const handleConfirmAvatar = async () => {
    if (!editingEmployee || !pendingAvatarUrl) return;
    setAvatarConfirming(true);
    try {
      await updateAvatar(editingEmployee.id, pendingAvatarUrl);
      message.success('修改成功');
      setAvatarModalVisible(false);
      setPendingAvatarUrl('');
      setEditingEmployee(null);
      fetchEmployees();
    } catch (e: any) {
      message.error(e.message);
    } finally {
      setAvatarConfirming(false);
    }
  };

  const beforeUpload = (file: RcFile): boolean | typeof Upload.LIST_IGNORE => {
    if (!file.type.startsWith('image/')) {
      message.error('只支持图片文件');
      return Upload.LIST_IGNORE;
    }
    if (file.size > 5 * 1024 * 1024) {
      message.error('图片大小不能超过 5MB');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  // antd Upload 的 customRequest：上传到 xmarsLive 拿 URL，仅做预览，不落库
  const customAvatarUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    if (!editingEmployee) {
      onError?.(new Error('未选择员工'));
      return;
    }
    setAvatarUploading(true);
    try {
      const url = await uploadAvatarImage(editingEmployee.id, file as File);
      setPendingAvatarUrl(url);
      message.success('上传成功，请确认修改');
      onSuccess?.({ url }, file as any);
    } catch (e: any) {
      message.error(e.message);
      onError?.(e);
    } finally {
      setAvatarUploading(false);
    }
  };

  const columns = [
    {
      title: '头像', dataIndex: 'avatar', key: 'avatar', width: 60,
      render: (avatar: string | null) => (
        <Avatar size={36} src={avatar || undefined} icon={!avatar ? <UserOutlined /> : undefined} />
      ),
    },
    { title: '昵称', dataIndex: 'userName', key: 'userName', width: 150 },
    { title: '用户ID', dataIndex: 'userId', key: 'userId', width: 120 },
    {
      title: '状态', key: 'status', width: 100,
      render: (_: any, record: BotEmployee) => (
        <Space>
          {record.status === 1 ? <Tag color="success">启用</Tag> : <Tag color="error">禁用</Tag>}
          {record.isLoggedIn ? <Tag color="blue">在线</Tag> : <Tag>离线</Tag>}
        </Space>
      ),
    },
    { title: '添加时间', dataIndex: 'createTime', key: 'createTime', width: 170 },
    {
      title: '操作', key: 'action', width: 240,
      render: (_: any, record: BotEmployee) => (
        <Space>
          <Button type="link" size="small" onClick={() => openNicknameModal(record)}>
            修改昵称
          </Button>
          <Button type="link" size="small" onClick={() => openAvatarModal(record)}>
            修改头像
          </Button>
          <Popconfirm title="确认移除该员工？" onConfirm={() => handleRemove(record.id)}>
            <Button type="link" size="small" danger>
              移除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const searchColumns = [
    {
      title: '头像', dataIndex: 'avatar', key: 'avatar', width: 60,
      render: (avatar: string | null) => (
        <Avatar size={36} src={avatar || undefined} icon={!avatar ? <UserOutlined /> : undefined} />
      ),
    },
    { title: '昵称', dataIndex: 'userName', key: 'userName', width: 150 },
    { title: '用户ID', dataIndex: 'userId', key: 'userId', width: 120 },
    {
      title: '状态', key: 'status', width: 100,
      render: (_: any, record: AvailableBot) => (
        <Space>
          {record.status === 1 ? <Tag color="success">启用</Tag> : <Tag color="error">禁用</Tag>}
          {record.isLoggedIn ? <Tag color="blue">在线</Tag> : <Tag>离线</Tag>}
        </Space>
      ),
    },
    {
      title: '操作', key: 'action', width: 100,
      render: (_: any, record: AvailableBot) => (
        <Button
          type="primary"
          size="small"
          loading={addingBotId === record.id}
          onClick={() => handleAdd(record.id)}
        >
          添加
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="专属机器人员工" />
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setAddModalVisible(true); setAvailableBots([]); setSearchKeyword(''); }}>
          添加员工
        </Button>
      </div>
      <Table
        dataSource={employees}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="middle"
        pagination={{ pageSize: 20 }}
      />

      {/* 添加员工 Modal */}
      <Modal
        title="添加员工"
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        footer={null}
        width={700}
      >
        <Input.Search
          placeholder="搜索机器人昵称"
          allowClear
          enterButton={<><SearchOutlined /> 搜索</>}
          value={searchKeyword}
          onChange={e => setSearchKeyword(e.target.value)}
          onSearch={handleSearch}
          style={{ marginBottom: 16 }}
        />
        <Table
          dataSource={availableBots}
          columns={searchColumns}
          rowKey="id"
          loading={searchLoading}
          size="small"
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: '输入关键词搜索可分配的机器人' }}
        />
      </Modal>

      {/* 修改昵称 Modal */}
      <Modal
        title="修改昵称"
        open={nicknameModalVisible}
        onCancel={() => { setNicknameModalVisible(false); nicknameForm.resetFields(); setEditingEmployee(null); }}
        onOk={() => nicknameForm.submit()}
        okText="确认修改"
      >
        <Form form={nicknameForm} layout="vertical" onFinish={handleUpdateNickname}>
          <Form.Item name="nickname" label="新昵称" rules={[{ required: true, message: '请输入新昵称' }]}>
            <Input placeholder="请输入新昵称" maxLength={50} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 修改头像 Modal */}
      <Modal
        title="修改头像"
        open={avatarModalVisible}
        onCancel={closeAvatarModal}
        onOk={handleConfirmAvatar}
        okText="确认修改"
        cancelText="取消"
        okButtonProps={{ disabled: !pendingAvatarUrl || avatarUploading, loading: avatarConfirming }}
        cancelButtonProps={{ disabled: avatarUploading || avatarConfirming }}
        maskClosable={!avatarUploading && !avatarConfirming}
        closable={!avatarUploading && !avatarConfirming}
        destroyOnClose
        width={520}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24, marginBottom: 20 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>当前</div>
            <Avatar
              size={72}
              src={editingEmployee?.avatar || undefined}
              icon={!editingEmployee?.avatar ? <UserOutlined /> : undefined}
            />
          </div>
          <div style={{ fontSize: 22, color: '#bbb' }}>→</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>预览</div>
            <Avatar
              size={72}
              src={pendingAvatarUrl || undefined}
              icon={!pendingAvatarUrl ? <UserOutlined /> : undefined}
            />
          </div>
        </div>

        <Upload.Dragger
          name="file"
          accept="image/*"
          multiple={false}
          showUploadList={false}
          beforeUpload={beforeUpload}
          customRequest={customAvatarUpload}
          disabled={avatarUploading || avatarConfirming}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">
            {avatarUploading ? '上传中…' : pendingAvatarUrl ? '已上传，可重新选择' : '点击或拖拽图片到此区域上传'}
          </p>
          <p className="ant-upload-hint" style={{ fontSize: 12, color: '#888' }}>
            支持 jpg/png/gif/webp，单文件不超过 5MB；上传完成后请点击"确认修改"
          </p>
        </Upload.Dragger>

        {pendingAvatarUrl && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#888', wordBreak: 'break-all' }}>
            URL: <code>{pendingAvatarUrl}</code>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BotEmployeePage;
